import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Catálogo del Centro de Medicare (`/medicare-center`).
 *
 * **La pantalla es casi la lista de pólizas con otro filtro**, así que la mayor
 * parte del texto reusa `policies.list.*` y `common.*`: solo se crean llaves
 * para lo que es propio de Medicare (los filtros de elegibilidad, la sincronía
 * con HealthSherpa y tres cabeceras de columna). Namespace propio:
 * `policies.medicare.*`, dentro de la familia de pólizas.
 *
 * No se traducen: `HealthSherpa` ni `Medicare` (marcas y nombre de programa),
 * los nombres de aseguradora, plan y producto (datos de BD) ni los estados de
 * póliza que sirve el backend.
 */
export const MEDICARE_MESSAGES = defineMessages({
  // ── Propio de esta pantalla ──
  title: {id: 'MENU.item.medicareCenter', defaultMessage: 'Medicare Center'},
  contactsTitle: {id: 'policies.medicare.contactsTitle', defaultMessage: 'Medicare Contacts'},
  noPermission: {
    id: 'policies.medicare.noPermission',
    defaultMessage: "You don't have permission to view Medicare Center.",
  },
  searchByName: {id: 'policies.medicare.searchByName', defaultMessage: 'Search by name'},
  myContactsOnly: {
    id: 'policies.medicare.myContactsOnly',
    defaultMessage: 'Show my contacts only',
  },
  medicareFilter: {id: 'policies.medicare.filter.label', defaultMessage: 'Medicare Filter'},
  colBirthdayAge: {id: 'policies.medicare.col.birthdayAge', defaultMessage: 'Birthday / Age'},

  /**
   * Etiquetas de los chips de «filtros aplicados» y de los rangos de fecha.
   * Todas reusan llaves de `contacts.*` y `common.*`: es el mismo juego de
   * filtros que la ficha de contactos (plan §10.8).
   */
  chipUser: {id: 'common.field.user', defaultMessage: 'User'},
  chipPreferredLanguage: {
    id: 'contacts.profile.field.preferredLanguage',
    defaultMessage: 'Preferred Language',
  },
  chipBirthMonth: {
    id: 'contacts.filters.field.birthMonth',
    defaultMessage: 'Date of Birth Month',
  },
  chipAgeFrom: {id: 'contacts.filters.field.ageFrom', defaultMessage: 'Age from'},
  chipAgeTo: {id: 'contacts.filters.field.ageTo', defaultMessage: 'Age to'},
  chipGender: {id: 'common.field.gender', defaultMessage: 'Gender'},
  chipState: {id: 'common.field.state', defaultMessage: 'State'},
  chipZipCode: {id: 'common.field.zipCode', defaultMessage: 'Zip Code'},
  chipTags: {id: 'contacts.filters.field.tags', defaultMessage: 'Tags'},
  chipSignatureStatus: {
    id: 'contacts.filters.field.signatureStatus',
    defaultMessage: 'Signature Form Status',
  },
  chipSignatureForm: {
    id: 'contacts.filters.field.signatureForm',
    defaultMessage: 'Signature Form',
  },
  chipCarrier: {id: 'contacts.filters.field.carrier', defaultMessage: 'Carrier'},
  chipPolicyStatus: {
    id: 'contacts.filters.field.policyStatus',
    defaultMessage: 'Policy Status',
  },
  chipContactCreated: {
    id: 'contacts.page.dateRange.contactCreated',
    defaultMessage: 'Contact Created',
  },
  chipEffectiveDate: {
    id: 'contacts.page.dateRange.effectiveDate',
    defaultMessage: 'Effective Date',
  },
  chipPolicyCreated: {
    id: 'contacts.page.dateRange.policyCreated',
    defaultMessage: 'Policy Created Date',
  },
  chipSubmissionDate: {
    id: 'contacts.page.dateRange.submissionDate',
    defaultMessage: 'Submission Date',
  },
  colMemberPolicy: {id: 'policies.medicare.col.memberPolicy', defaultMessage: 'Member Policy'},
  colLastPolicy: {id: 'policies.medicare.col.lastPolicy', defaultMessage: 'Last Policy'},
  /** `{years}` ya viene calculado; la abreviatura cambia de idioma. */
  ageYears: {id: 'policies.medicare.ageYears', defaultMessage: '{years} yrs'},
  activePolicyLine: {
    id: 'policies.medicare.activePolicyLine',
    defaultMessage: 'Active policy line: {value}',
  },
  noPolicy: {id: 'policies.medicare.noPolicy', defaultMessage: 'No policy'},
  emptyContacts: {
    id: 'policies.medicare.empty',
    defaultMessage: 'There are no Medicare contacts currently added',
  },
  badgeNoMedicare: {
    id: 'policies.medicare.badge.noMedicare',
    defaultMessage: '65+ without Medicare',
  },
  badgeTurning65: {
    id: 'policies.medicare.badge.turning65',
    defaultMessage: '65 in <= 3 months',
  },
  /** El `catch` del exportador muestra este `.message` en un toast. */
  errExport: {
    id: 'policies.medicare.error.export',
    defaultMessage: 'Error exporting Medicare data',
  },
  moreInformation: {
    id: 'policies.list.page.tooltip.moreInfo',
    defaultMessage: 'More information:',
  },

  // ── HealthSherpa ──
  hsSyncedTitle: {
    id: 'policies.medicare.hs.syncedTitle',
    defaultMessage: 'Syncronized with HealthSherpa',
  },
  hsSyncTitle: {
    id: 'policies.medicare.hs.syncTitle',
    defaultMessage: 'Syncronize HealthSherpa',
  },
  hsSync: {id: 'policies.medicare.hs.sync', defaultMessage: 'Sync HS'},
  hsResync: {id: 'policies.medicare.hs.resync', defaultMessage: 'Re-Sync HealthSherpa'},
  hsSearching: {id: 'policies.medicare.hs.searching', defaultMessage: 'Searching HS...'},
  hsSynced: {id: 'policies.medicare.hs.synced', defaultMessage: 'HS Synced'},

  // ── Reusadas del catálogo de pólizas y del común ──
  newPolicy: {id: 'policies.list.page.btn.newPolicy', defaultMessage: 'New Policy'},
  export: {id: 'policies.list.page.btn.export', defaultMessage: 'Export'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},
  filter: {id: 'common.action.filter', defaultMessage: 'Filter'},
  manageTags: {id: 'comms.tags.manage', defaultMessage: 'Manage Tags'},
  addNote: {id: 'policies.list.page.act.addNote', defaultMessage: 'Add Note'},
  duplicate: {id: 'comms.campaigns.templates.duplicate', defaultMessage: 'Duplicate'},
  preview: {id: 'common.action.preview', defaultMessage: 'Preview'},
  effectiveYear: {id: 'comms.recipients.page.effectiveYear', defaultMessage: 'Effective year:'},
  allOrganizations: {
    id: 'contacts.page.allOrganizations',
    defaultMessage: 'All Organizations',
  },
  immigrationStatus: {
    id: 'policies.list.filters.field.immigrationStatus',
    defaultMessage: 'Immigration Status',
  },
  colClient: {id: 'contacts.list.col.client', defaultMessage: 'Client'},
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  tags: {id: 'contacts.filters.field.tags', defaultMessage: 'Tags'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  labelProduct: {id: 'policies.list.page.ir.product', defaultMessage: 'Product:'},
  labelPlan: {id: 'policies.list.page.ir.plan', defaultMessage: 'Plan:'},
  labelMembers: {id: 'telephony.groups.membersHeading', defaultMessage: 'Members:'},
  labelCarrier: {id: 'policies.list.page.cell.carrier', defaultMessage: 'Carrier:'},
  labelStatus: {id: 'admin.users.label.status', defaultMessage: 'Status:'},
  labelDocuments: {id: 'policies.list.page.cell.documents', defaultMessage: 'Documents:'},
  labelPayment: {id: 'policies.list.page.cell.payment', defaultMessage: 'Payment:'},
  labelEffective: {id: 'contacts.profile.policies.effective', defaultMessage: 'Effective:'},
  labelSubmitted: {id: 'policies.list.page.ir.submitted', defaultMessage: 'Submitted:'},
  labelAgent: {id: 'contacts.comms.call.agent', defaultMessage: 'Agent:'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
})

/** Marcadores de celda vacía. Símbolos, no texto. */
export const DASH = '-'
export const EM_DASH = '—'
/** Nombre del producto de terceros. No se traduce. */
export const HEALTHSHERPA = 'HealthSherpa'
/** Nombre del programa federal. No se traduce. */
export const MEDICARE = 'Medicare'

type Labels = Record<string, MessageDescriptor>

/** Refleja `MEDICARE_FILTER_OPTIONS`. El `value` viaja al filtro de la API. */
const MEDICARE_FILTER: Labels = defineMessages({
  ALL_ELIGIBLE: {id: 'common.pagination.all', defaultMessage: 'All'},
  AGE_65_PLUS: {
    id: 'policies.medicare.filter.age65Plus',
    defaultMessage: 'Eligible Medicare (65+)',
  },
  TURNING_65: {
    id: 'policies.medicare.filter.turning65',
    defaultMessage: 'Eligible <65 (<= 3 months to 65)',
  },
  AGE_64_NOT_APPROACHING: {
    id: 'policies.medicare.filter.age64',
    defaultMessage: 'Age 64 – More than 3 months to 65',
  },
  HAS_MEDICARE: {
    id: 'policies.medicare.filter.hasMedicare',
    defaultMessage: 'Has Medicare (Active policy)',
  },
}) as Labels

/** Refleja `IMMIGRATION_QUICK_FILTER_OPTIONS`. */
const IMMIGRATION_QUICK_FILTER: Labels = defineMessages({
  CITIZEN_OR_RESIDENT: {
    id: 'policies.medicare.immigrationFilter.citizenOrResident',
    defaultMessage: 'Only Citizen or Resident',
  },
  CITIZEN: {
    id: 'policies.medicare.immigrationFilter.citizen',
    defaultMessage: 'Only Citizen',
  },
  RESIDENT: {
    id: 'policies.medicare.immigrationFilter.resident',
    defaultMessage: 'Only Resident',
  },
  ANY: {id: 'policies.list.filters.placeholder.any', defaultMessage: 'Any'},
}) as Labels

/**
 * Ramo de la poliza. `getPolicyLineName` los devolvia en literal desde un helper
 * de modulo: ni el rule ni el paso 6 del gate ven un `return`.
 * `P&C` y `ACA` son siglas y se escriben igual en los dos idiomas.
 */
const POLICY_LINE: Labels = defineMessages({
  Dental: {id: 'policies.line.dental', defaultMessage: 'Dental'},
  Vision: {id: 'policies.line.vision', defaultMessage: 'Vision'},
  Health: {id: 'policies.line.health', defaultMessage: 'Health'},
  Medicare: {id: 'policies.line.medicare', defaultMessage: 'Medicare'},
}) as Labels

/**
 * Rol del contacto en la poliza. El valor ingles sigue siendo la clave de
 * estado (`policyInfo.role === 'Holder'` decide que se pinta), asi que solo se
 * traduce al renderizar.
 */
const CONTACT_ROLE: Labels = defineMessages({
  Holder: {id: 'shell.search.role.holder', defaultMessage: 'Holder'},
  Family: {id: 'shell.search.role.family', defaultMessage: 'Family'},
}) as Labels

export const policyLineLabel = (intl: IntlShape, value?: string | null) =>
  value && POLICY_LINE[value] ? intl.formatMessage(POLICY_LINE[value]) : value ?? ''
export const contactRoleLabel = (intl: IntlShape, value?: string | null) =>
  value && CONTACT_ROLE[value] ? intl.formatMessage(CONTACT_ROLE[value]) : value ?? ''

/** Los doce meses, para el filtro de mes de nacimiento. */
const MONTHS: Labels = defineMessages({
  '1': {id: 'common.month.january', defaultMessage: 'January'},
  '2': {id: 'common.month.february', defaultMessage: 'February'},
  '3': {id: 'common.month.march', defaultMessage: 'March'},
  '4': {id: 'common.month.april', defaultMessage: 'April'},
  '5': {id: 'common.month.may', defaultMessage: 'May'},
  '6': {id: 'common.month.june', defaultMessage: 'June'},
  '7': {id: 'common.month.july', defaultMessage: 'July'},
  '8': {id: 'common.month.august', defaultMessage: 'August'},
  '9': {id: 'common.month.september', defaultMessage: 'September'},
  '10': {id: 'common.month.october', defaultMessage: 'October'},
  '11': {id: 'common.month.november', defaultMessage: 'November'},
  '12': {id: 'common.month.december', defaultMessage: 'December'},
}) as Labels

/** Idioma preferido del contacto: es un dato del contacto, no el idioma de la app. */
const PREFERRED_LANGUAGE: Labels = defineMessages({
  Spanish: {id: 'common.language.spanish', defaultMessage: 'Spanish'},
  English: {id: 'common.language.english', defaultMessage: 'English'},
}) as Labels

/** Estado del formulario de firma. */
const SIGNATURE_STATUS: Labels = defineMessages({
  SIGNED: {id: 'common.status.signed', defaultMessage: 'Signed'},
  NOT_SIGNED: {id: 'contacts.filters.status.notSigned', defaultMessage: 'Did not sign'},
}) as Labels

const options = (intl: IntlShape, map: Labels) =>
  Object.keys(map).map((value) => ({value, label: intl.formatMessage(map[value])}))

export const medicareFilterOptions = (intl: IntlShape) => options(intl, MEDICARE_FILTER)
export const immigrationQuickFilterOptions = (intl: IntlShape) =>
  options(intl, IMMIGRATION_QUICK_FILTER)
export const monthOptions = (intl: IntlShape) => options(intl, MONTHS)
export const preferredLanguageOptions = (intl: IntlShape) => options(intl, PREFERRED_LANGUAGE)
export const signatureStatusOptions = (intl: IntlShape) => options(intl, SIGNATURE_STATUS)
